import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import {
  BM_PAYMENT_RESPONSE_STATUS,
  DashboardMetric,
  LISTING_METRICS_PAYMENT_STATUS,
} from '../../types';

import './PaymentStatus.styles.scss';

const BM_PAYMENT_RESPONSE_COPY = {
  [BM_PAYMENT_RESPONSE_STATUS.ACCEPTED]: 'invoiceAccepted',
  [BM_PAYMENT_RESPONSE_STATUS.PENDING]: 'forReview',
  [BM_PAYMENT_RESPONSE_STATUS.REJECTED]: 'invoiceRejected',
};

type PaymentStatusProps = {
  className?: string;
  dashboardMetric: DashboardMetric;
  openInvoiceModal: (DashboardMetric: DashboardMetric) => void;
};

const PaymentStatus: React.FC<PaymentStatusProps> = (props) => {
  const { className, dashboardMetric, openInvoiceModal } = props;

  const {
    isUltraAdmin,
    currentUser: { _id: currentUserId },
  } = useContext(CurrentUserContext);

  const classes = classNames('payment-status', className);

  const { paymentStatus, user, bmPaymentResponse } = dashboardMetric;

  const { t } = useTranslation();

  const isInvoiceSkipped = useMemo(
    () => paymentStatus === LISTING_METRICS_PAYMENT_STATUS.INVOICE_SKIPPED,
    [paymentStatus],
  );

  const showReviewButton = useMemo(
    () =>
      isInvoiceSkipped &&
      bmPaymentResponse !== BM_PAYMENT_RESPONSE_STATUS.ACCEPTED,
    [bmPaymentResponse, isInvoiceSkipped],
  );

  const isSkippedByMe = useMemo(
    () =>
      isUltraAdmin &&
      user?._id === currentUserId &&
      paymentStatus === LISTING_METRICS_PAYMENT_STATUS.SUPER_ADMIN_SKIPPED,
    [currentUserId, isUltraAdmin, paymentStatus, user?._id],
  );

  const paymentLabel = useMemo(
    () =>
      isSkippedByMe
        ? 'youSkippedInvoice'
        : isInvoiceSkipped
        ? BM_PAYMENT_RESPONSE_COPY[bmPaymentResponse]
        : paymentStatus,
    [isSkippedByMe, paymentStatus, isInvoiceSkipped, bmPaymentResponse],
  );

  return (
    <div className={classes}>
      {t(paymentLabel)}

      {showReviewButton && (
        <Button
          variant="link"
          onClick={(e) => {
            e.stopPropagation();
            openInvoiceModal(dashboardMetric);
          }}
        >
          {t('review')}
        </Button>
      )}
    </div>
  );
};

export default PaymentStatus;
